import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * ProtectedRoute — redirects unauthenticated users to /authentication.
 * Preserves the current path in `?next=` so they can be sent back after sign-in.
 *
 * Usage:
 *   <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
 *
 * For admin-only routes, pass requireAdmin:
 *   <Route path="/super-admin-dashboard" element={<ProtectedRoute requireAdmin><SuperAdminDashboard /></ProtectedRoute>} />
 */
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();

  // While auth state is being determined, render nothing (avoid flash redirect)
  if (loading) {
    return null;
  }

  // Not signed in — redirect to the authentication page
  if (!user) {
    return (
      <Navigate
        to={`/authentication?next=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  // Route requires admin role
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default ProtectedRoute;
